import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {DatePicker, Select, Space, Button, Tooltip} from 'antd';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {
  StyledTableFiltersSearch,
  StyledTableFilters,
  StyledTableFiltersElements,
  StyledTableFiltersLeftSideContent
} from './StyledTableFilters';
import {
  formHelpers,
  InvoicePaymentStatusesOptions,
  objectHelpers,
  tableHelpers
} from '../../../../../utils/helpers';
import {CalendarOutlined} from '@ant-design/icons';

const TableFilters = ({
  defaultQuery = null,
  filterFields = ['search', 'date_range', 'status'],
  onFilter,
  leftSideContent,
  rightSideContent,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [values, setValues] = useState({
    start_date: null,
    end_date: null,
    date_range: null,
    payment_statuses: [],
    search: ''
  });

  const [statusOptions, ] = useState(InvoicePaymentStatusesOptions({excludePaidOutsideProcessing: true}));

  // new state: control whether RangePicker works in 'month' or 'year' mode
  const [pickerMode, setPickerMode] = useState('month'); // 'month' or 'year'

  const isMonthMode = pickerMode === 'month';

  const togglePickerMode = (e) => {
    e?.stopPropagation && e.stopPropagation();

    const nextMode = pickerMode === 'month' ? 'year' : 'month';

    // Convert existing start/end values (if any) to the new granularity
    const { start_date, end_date } = values;

    if (start_date || end_date) {
      const newStart = start_date ? start_date.startOf(nextMode) : null;

      // If switching to month mode, prefer to calculate newEnd based on start_date
      // so the range represents the full month of the selected start date.
      let newEnd = null;
      if (nextMode === 'month') {
        newEnd = start_date ? start_date.endOf('month') : (end_date ? end_date.endOf('month') : null);
      } else {
        newEnd = end_date ? end_date.endOf(nextMode) : null;
      }

      // update local values so DatePicker shows converted values
      setValues(prev => ({ ...prev, start_date: newStart, end_date: newEnd }));

      // prepare formatted payload using existing helper and notify parent
      const { formattedValues } = tableHelpers.formatFilterPayload('date_range', [newStart, newEnd]);
      onFilter?.(formattedValues);
    }

    setPickerMode(nextMode);
  }

  // Map incoming defaultQuery.date_gte / date_lte (strings) to dayjs and store as start_date/end_date only when present
  useEffect(() => {
    if (!defaultQuery || objectHelpers.isEmptyObject(defaultQuery)) return;

    const start = defaultQuery.date_gte ? dayjs(defaultQuery.date_gte) : null;
    const end = defaultQuery.date_lte ? dayjs(defaultQuery.date_lte) : null;

    const mapped = {
      ...defaultQuery,
      start_date: start,
      end_date: end,
    };

    setValues(prev => ({ ...prev, ...mapped }));
  }, [defaultQuery]); // eslint-disable-line react-hooks/exhaustive-deps


  const isEnabledField = (key) => filterFields.includes(key);

  const handleOnFilter = (key, value) => {
    const { updatedValues, formattedValues } = tableHelpers.formatFilterPayload(key, value);
    setValues(prev => ({ ...prev, ...updatedValues }));
    onFilter?.(formattedValues);
  }

  const handleOnFilterDateRange = (key, value) => {
    let startObj = null;
    let endObj = null;

    if (value) {
      if (pickerMode === 'month') {
        startObj = value.startOf('month');
        endObj = value.endOf('month');
      } else if (pickerMode === 'year') {
        startObj = value.startOf('year');
        endObj = value.endOf('year');
      } else {
        startObj = value.startOf('day');
        endObj = value.endOf('day');
      }
    }

    // return date objects (dayjs instances) instead of ISO strings
    handleOnFilter(key, [startObj, endObj]);
  }

  // disabledDate must respect pickerMode granularity:
  // - month: disallow months after current month
  // - year: disallow years after current year
  // - default: disallow dates after today
  const disabledDate = (current) => {
    if (!current) return false;
    if (pickerMode === 'month') {
      return current.isAfter(dayjs(), 'month');
    }
    if (pickerMode === 'year') {
      return current.isAfter(dayjs(), 'year');
    }
    return current.isAfter(dayjs(), 'day');
  };

  const getDatePicker = () => {
    const datePickerRangeFormat = isMonthMode ? 'MMM YYYY' : 'YYYY';

    const datePickerPrefix = (
      <Tooltip title={t(isMonthMode ? 'switchToYear' : 'switchToMonth')}>
        <Button type='text' size='small' onClick={togglePickerMode} aria-label='toggle date picker mode' icon={<CalendarOutlined />}>
          {t(isMonthMode ? 'month' : 'year')}
        </Button>
      </Tooltip>
    );

    return (
      <Space align='center' size={8}>
        <DatePicker
          disabledDate={disabledDate}
          format={datePickerRangeFormat}
          onChange={(value) => handleOnFilterDateRange('date_range', value)}
          picker={pickerMode}
          prefix={datePickerPrefix}
          size='large'
          suffixIcon={null}
          value={[values.start_date, values.end_date]}
        />
      </Space>
    )
  }

  return (
    <StyledTableFilters {...rest}>
      <StyledTableFiltersElements>
        <Space size='large' align='center'>
          <Space size='small' align='center'>
            {isEnabledField('search') && (
              <StyledTableFiltersSearch
                allowClear
                onSearch={(value) => handleOnFilter('search', value)}
                placeholder={t('search')}
                size='large'
                value={values.search}
              />
            )}
            {isEnabledField('status') && (
              <Select
                allowClear
                mode='multiple'
                onChange={(value) => handleOnFilter('payment_statuses', value)}
                options={formHelpers.getStatusOptions(statusOptions)}
                placeholder={t('allStatuses')}
                popupMatchSelectWidth={false}
                maxTagCount={1}
                size='large'
                style={{ minWidth: '150px' }}
                value={values.payment_statuses}
              />
            )}
            {isEnabledField('date_range') && getDatePicker()}
          </Space>
          {leftSideContent && (
            <StyledTableFiltersLeftSideContent className='table-filters-left-side-content'>
              {leftSideContent}
            </StyledTableFiltersLeftSideContent>
          )}
        </Space>
        {rightSideContent && <div className='table-filters-right-side-content'>{rightSideContent}</div>}
      </StyledTableFiltersElements>
    </StyledTableFilters>
  );
}

TableFilters.propTypes = {
  defaultQuery: PropTypes.object,
  filterFields: PropTypes.arrayOf(PropTypes.string),
  leftSideContent: PropTypes.any,
  onFilter: PropTypes.func,
  rightSideContent: PropTypes.any
}

export default TableFilters;
